import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RootState } from '../store';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import { getRoleBasedNavigation } from '../utils/roleBasedAccess';

const SidebarContainer = styled.nav`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${Colors.surface};
  border-right: 1px solid ${Colors.border};
`;

const PowerBIBranding = styled.div`
  padding: ${Spacing.lg}px ${Spacing.lg}px ${Spacing.xl}px;
  border-bottom: 1px solid ${Colors.borderLight};
  background: linear-gradient(90deg, ${Colors.primary} 0%, ${Colors.secondary} 100%);
  color: ${Colors.textOnPrimary};
`;

const PowerBILogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${Colors.textOnPrimary};
  border-radius: ${BorderRadius.md}px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${Spacing.md}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const BrandText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompanyName = styled.h1`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textOnPrimary};
  margin: 0;
  line-height: 1.2;
`;

const AppName = styled.p`
  font-size: ${FontSizes.sm}px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-weight: ${FontWeights.normal};
`;

const WorkspaceInfo = styled.div`
  font-size: ${FontSizes.xs}px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const WorkspaceIcon = styled.span`
  font-size: ${FontSizes.sm}px;
`;

const NavigationSection = styled.div`
  padding: ${Spacing.lg}px ${Spacing.md}px;
  flex: 1;
  overflow-y: auto;
`;

const SectionTitle = styled.h2`
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.md}px;
  padding: 0 ${Spacing.sm}px;
`;

const NavigationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${Spacing.xl}px 0;
`;

const NavigationItem = styled.li`
  margin-bottom: ${Spacing.xs}px;
`;

const NavigationLink = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: ${Spacing.md}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.md}px;
  text-decoration: none;
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.normal};
  transition: all 150ms ease;
  position: relative;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }

  &.active {
    background-color: ${Colors.surfaceSelected};
    color: ${Colors.primary};
    font-weight: ${FontWeights.semibold};
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 20px;
      background-color: ${Colors.primary};
      border-radius: 0 2px 2px 0;
    }
  }
`;

const NavigationIcon = styled.span`
  font-size: ${FontSizes.lg}px;
  margin-right: ${Spacing.md}px;
  width: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavigationText = styled.span`
  flex: 1;
`;

const StatusBadge = styled.span<{ variant: 'warning' | 'error' | 'info' }>`
  background-color: ${props => {
    switch (props.variant) {
      case 'warning': return Colors.warning;
      case 'error': return Colors.danger;
      case 'info': return Colors.info;
      default: return Colors.info;
    }
  }};
  color: ${Colors.textOnPrimary};
  font-size: ${FontSizes.xs}px;
  padding: 2px 6px;
  border-radius: ${BorderRadius.round}px;
  font-weight: ${FontWeights.semibold};
  min-width: 18px;
  text-align: center;
`;

const FooterSection = styled.div`
  padding: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.borderLight};
  background-color: ${Colors.backgroundDark};
`;

const PowerBIBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
  text-align: center;
  gap: ${Spacing.xs}px;
  line-height: 1.4;
`;

const PowerBISidebar: React.FC = () => {
  const { currentRole } = useSelector((state: RootState) => state.user);
  const { t } = useTranslation();

  // Get role-based navigation items
  const roleBasedNavigation = getRoleBasedNavigation(currentRole);

  // Map role-based navigation to PowerBI sidebar format with translations
  const reportItems = roleBasedNavigation.map(nav => {
    // Add badge for AI Talent Insights if user has access
    const badge = nav.path === '/insights' ? { text: '5', variant: 'warning' as const } : null;
    
    // Map paths to translation keys
    const labelKey = nav.path.replace('/', '').replace('-', '');
    
    return {
      path: nav.path,
      icon: '•',
      label: t(`navigation.${labelKey}`, nav.label), // fallback to original label
      badge,
    };
  });

  const dataItems = [
    {
      path: '/data-sources',
      icon: '•',
      label: t('navigation.dataSources'),
      badge: { text: '1', variant: 'error' as const },
    },
  ];

  return (
    <SidebarContainer>
      <PowerBIBranding>
        <PowerBILogo>
          <LogoIcon>
            <LogoImage 
              src="/assets/Clouds On Mars - Signet - Martian Orange.png" 
              alt="Clouds on Mars Logo"
            />
          </LogoIcon>
          <BrandText>
            <CompanyName>{t('header.workspace')}</CompanyName>
            <AppName>HR Analytics</AppName>
          </BrandText>
        </PowerBILogo>
        <WorkspaceInfo>
          <WorkspaceIcon>W</WorkspaceIcon>
          <span>Workspace • Premium</span>
        </WorkspaceInfo>
      </PowerBIBranding>

      <NavigationSection>
        <SectionTitle>{t('common.reports', 'Reports')}</SectionTitle>
        <NavigationList>
          {reportItems.map((item) => (
            <NavigationItem key={item.path}>
              <NavigationLink
                to={item.path}
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <NavigationIcon>{item.icon}</NavigationIcon>
                <NavigationText>{item.label}</NavigationText>
                {item.badge && (
                  <StatusBadge variant={item.badge.variant}>
                    {item.badge.text}
                  </StatusBadge>
                )}
              </NavigationLink>
            </NavigationItem>
          ))}
        </NavigationList>

        <SectionTitle>{t('common.data', 'Data')}</SectionTitle>
        <NavigationList>
          {dataItems.map((item) => (
            <NavigationItem key={item.path}>
              <NavigationLink
                to={item.path}
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <NavigationIcon>{item.icon}</NavigationIcon>
                <NavigationText>{item.label}</NavigationText>
                {item.badge && (
                  <StatusBadge variant={item.badge.variant}>
                    {item.badge.text}
                  </StatusBadge>
                )}
              </NavigationLink>
            </NavigationItem>
          ))}
        </NavigationList>
      </NavigationSection>

      <FooterSection>
        <PowerBIBadge>
          <div>Powered by</div>
          <div style={{ fontWeight: FontWeights.semibold, fontSize: `${FontSizes.sm}px` }}>
            Microsoft Fabric platform
          </div>
          <div style={{ fontWeight: FontWeights.semibold, fontSize: `${FontSizes.sm}px` }}>
            and Power BI
          </div>
        </PowerBIBadge>
      </FooterSection>
    </SidebarContainer>
  );
};

export default PowerBISidebar;